import { useState } from "react";
import { Prestamo, CuotaPrestamo } from "@/pages/prestamos/types";
import Badge from "@/components/base/Badge";

interface Props {
  prestamo: Prestamo;
  onClose: () => void;
  onSetStatus: (status: Prestamo["estado"]) => Promise<void>;
  onUpdateCuota: (
    cuotaId: string,
    payload: { status: CuotaPrestamo["estado"]; paidAt?: string | null; reference?: string | null },
  ) => Promise<void>;
}

const TIPO_LABEL: Record<string, string> = {
  bancario: "Bancario",
  fosis: "FOSIS",
  subdere: "SUBDERE",
  gore: "GORE",
  particular: "Particular",
};

const TIPO_COLOR: Record<string, string> = {
  bancario: "bg-indigo-100 text-indigo-700",
  fosis: "bg-emerald-100 text-emerald-700",
  subdere: "bg-teal-100 text-teal-700",
  gore: "bg-violet-100 text-violet-700",
  particular: "bg-amber-100 text-amber-700",
};

const ESTADO_VARIANT: Record<string, "success" | "warning" | "error" | "default"> = {
  vigente: "success",
  pagado: "default",
  mora: "error",
  refinanciado: "warning",
};

const ESTADO_LABEL: Record<string, string> = {
  vigente: "Vigente",
  pagado: "Pagado",
  mora: "En mora",
  refinanciado: "Refinanciado",
};

export default function PrestamoDetalleModal({ prestamo, onClose, onSetStatus, onUpdateCuota }: Props) {
  const [tab, setTab] = useState<"resumen" | "cuotas">("resumen");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const formatCLP = (v: number) =>
    v.toLocaleString("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 });

  const saldo = prestamo.montoTotal - prestamo.montoPagado;
  const pct = Math.round((prestamo.montoPagado / prestamo.montoTotal) * 100);

  const cuotasPendientes = prestamo.cuotas.filter((c) => c.estado === "pendiente" || c.estado === "vencida");
  const cuotasPagadas = prestamo.cuotas.filter((c) => c.estado === "pagada");

  const cuotaEstadoVariant = (estado: CuotaPrestamo["estado"]): "success" | "warning" | "error" | "default" => {
    if (estado === "pagada") return "success";
    if (estado === "vencida") return "error";
    return "default";
  };

  const cuotaEstadoLabel = (estado: CuotaPrestamo["estado"]) => {
    if (estado === "pagada") return "Pagada";
    if (estado === "vencida") return "Vencida";
    return "Pendiente";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-50">
              <i className="ri-bank-line text-teal-600 text-lg" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-800">{prestamo.entidad}</h2>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TIPO_COLOR[prestamo.tipo]}`}>
                  {TIPO_LABEL[prestamo.tipo]}
                </span>
              </div>
              <p className="text-xs text-gray-400 font-mono">{prestamo.numero}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={prestamo.estado}
              disabled={saving}
              onChange={async (event) => {
                const next = event.target.value as Prestamo["estado"];
                setSaving(true);
                setError("");
                try {
                  await onSetStatus(next);
                } catch (e) {
                  setError(e instanceof Error ? e.message : "No se pudo actualizar estado");
                } finally {
                  setSaving(false);
                }
              }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white"
            >
              <option value="vigente">Vigente</option>
              <option value="mora">En mora</option>
              <option value="refinanciado">Refinanciado</option>
              <option value="pagado">Pagado</option>
            </select>
            <Badge variant={ESTADO_VARIANT[prestamo.estado]} size="sm">{ESTADO_LABEL[prestamo.estado]}</Badge>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6 flex-shrink-0">
          {(["resumen", "cuotas"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap capitalize ${
                tab === t ? "border-teal-500 text-teal-600" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t === "resumen" ? "Resumen" : `Cuotas (${prestamo.cuotas.length})`}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2">
              {error}
            </div>
          )}
          {tab === "resumen" && (
            <div className="space-y-5">
              {/* Propósito */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Propósito</p>
                <p className="text-sm text-gray-700">{prestamo.proposito}</p>
              </div>

              {/* Progreso */}
              <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Avance de pago</span>
                  <span className="text-sm font-bold text-teal-600">{pct}%</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${prestamo.estado === "pagado" ? "bg-emerald-500" : "bg-teal-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3 pt-1">
                  <div className="text-center">
                    <div className="text-base font-bold text-gray-800">{formatCLP(prestamo.montoTotal)}</div>
                    <div className="text-xs text-gray-400">Monto total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-base font-bold text-emerald-600">{formatCLP(prestamo.montoPagado)}</div>
                    <div className="text-xs text-gray-400">Pagado</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-base font-bold ${saldo > 0 ? "text-red-500" : "text-gray-400"}`}>{formatCLP(saldo)}</div>
                    <div className="text-xs text-gray-400">Saldo</div>
                  </div>
                </div>
              </div>

              {/* Datos */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Cuota mensual", value: formatCLP(prestamo.cuotasMensual), icon: "ri-calendar-line" },
                  { label: "Tasa de interés", value: `${prestamo.tasaInteres}% anual`, icon: "ri-percent-line" },
                  { label: "Cuotas pagadas", value: `${prestamo.cuotasPagadas} / ${prestamo.cuotasTotales}`, icon: "ri-checkbox-circle-line" },
                  { label: "Cuotas pendientes", value: `${prestamo.cuotasTotales - prestamo.cuotasPagadas}`, icon: "ri-time-line" },
                  { label: "Fecha inicio", value: prestamo.fechaInicio, icon: "ri-calendar-check-line" },
                  { label: "Fecha término", value: prestamo.fechaTermino, icon: "ri-calendar-close-line" },
                  { label: "Responsable", value: prestamo.responsable, icon: "ri-user-line" },
                  { label: "Garantía", value: prestamo.garantia ?? "Sin garantía", icon: "ri-shield-check-line" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 bg-gray-50 rounded-xl p-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white flex-shrink-0">
                      <i className={`${item.icon} text-sm text-gray-400`} />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">{item.label}</div>
                      <div className="text-sm font-semibold text-gray-700">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {prestamo.observaciones && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-amber-500" />
                  </div>
                  <p className="text-sm text-amber-700">{prestamo.observaciones}</p>
                </div>
              )}
            </div>
          )}

          {tab === "cuotas" && (
            <div className="space-y-4">
              {cuotasPendientes.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Pendientes / Vencidas ({cuotasPendientes.length})
                  </h3>
                  <div className="space-y-2">
                    {cuotasPendientes.map((c) => (
                      <div key={c.numero} className={`flex items-center justify-between p-3 rounded-xl border ${c.estado === "vencida" ? "border-red-200 bg-red-50/40" : "border-gray-100 bg-white"}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${c.estado === "vencida" ? "bg-red-100" : "bg-gray-100"}`}>
                            <span className={`text-xs font-bold ${c.estado === "vencida" ? "text-red-600" : "text-gray-500"}`}>#{c.numero}</span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-700">Vence: {c.fechaVencimiento}</div>
                            <div className="text-xs text-gray-400">Capital: {formatCLP(c.capital)} + Interés: {formatCLP(c.interes)}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={cuotaEstadoVariant(c.estado)} size="sm">{cuotaEstadoLabel(c.estado)}</Badge>
                          <span className="text-sm font-bold text-gray-800">{formatCLP(c.monto)}</span>
                          <button
                            disabled={saving}
                            onClick={async () => {
                              if (!c.id) return;
                              setSaving(true);
                              setError("");
                              try {
                                await onUpdateCuota(c.id, {
                                  status: c.estado === "pagada" ? "pendiente" : "pagada",
                                  paidAt: c.estado === "pagada" ? null : new Date().toISOString().slice(0, 10),
                                });
                              } catch (e) {
                                setError(e instanceof Error ? e.message : "No se pudo actualizar cuota");
                              } finally {
                                setSaving(false);
                              }
                            }}
                            className="px-2 py-1 text-xs rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-60"
                          >
                            {c.estado === "pagada" ? "Reabrir" : "Marcar pagada"}
                          </button>
                          {c.estado !== "vencida" && (
                            <button
                              disabled={saving}
                              onClick={async () => {
                                if (!c.id) return;
                                setSaving(true);
                                setError("");
                                try {
                                  await onUpdateCuota(c.id, { status: "vencida", paidAt: null });
                                } catch (e) {
                                  setError(e instanceof Error ? e.message : "No se pudo actualizar cuota");
                                } finally {
                                  setSaving(false);
                                }
                              }}
                              className="px-2 py-1 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60"
                            >
                              Marcar vencida
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cuotasPagadas.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Pagadas ({cuotasPagadas.length})
                  </h3>
                  <div className="space-y-2">
                    {cuotasPagadas.map((c) => (
                      <div key={c.numero} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50">
                            <i className="ri-check-line text-sm text-emerald-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-700">Cuota #{c.numero} — Pagada: {c.fechaPago}</div>
                            {c.referencia && <div className="text-xs text-gray-400 font-mono">{c.referencia}</div>}
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-emerald-600">{formatCLP(c.monto)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
